"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.useCohorts = exports.CohortsProvider = void 0;
const jsx_runtime_1 = require("react/jsx-runtime");
/**
 * CohortsProvider manages cohort state
 *
 * Provides normalized storage of cohorts with:
 * - Fetch cohorts by organization
 * - Fetch individual cohort by ID
 * - Selectors for filtered views
 */
const react_1 = require("react");
const cohortApi_1 = require("../helpers/cohortApi");
const CohortsContext = (0, react_1.createContext)(undefined);
/**
 * CohortsProvider component that manages cohort state
 */
const CohortsProvider = ({ children }) => {
    const [state, setState] = (0, react_1.useState)({
        items: {},
        loading: false,
        error: null,
    });
    /**
     * Fetch all cohorts for an organization
     */
    const fetchCohortsByOrg = (0, react_1.useCallback)((orgId) => __awaiter(void 0, void 0, void 0, function* () {
        setState((prev) => (Object.assign(Object.assign({}, prev), { loading: true, error: null })));
        try {
            const cohorts = yield cohortApi_1.cohortApi.getCohortsByOrg(orgId);
            // Normalize by ID
            const normalized = cohorts.reduce((acc, cohort) => {
                acc[cohort.id] = cohort;
                return acc;
            }, {});
            setState((prev) => (Object.assign(Object.assign({}, prev), { items: normalized, loading: false })));
        }
        catch (error) {
            setState((prev) => (Object.assign(Object.assign({}, prev), { loading: false, error: error instanceof Error ? error.message : 'Failed to fetch cohorts' })));
            throw error;
        }
    }), []);
    /**
     * Fetch a single cohort by ID
     */
    const fetchCohortById = (0, react_1.useCallback)((id) => __awaiter(void 0, void 0, void 0, function* () {
        setState((prev) => (Object.assign(Object.assign({}, prev), { loading: true, error: null })));
        try {
            const cohort = yield cohortApi_1.cohortApi.getCohortById(id);
            setState((prev) => (Object.assign(Object.assign({}, prev), { items: Object.assign(Object.assign({}, prev.items), { [cohort.id]: cohort }), loading: false })));
            return cohort;
        }
        catch (error) {
            setState((prev) => (Object.assign(Object.assign({}, prev), { loading: false, error: error instanceof Error ? error.message : 'Failed to fetch cohort' })));
            throw error;
        }
    }), []);
    /**
     * Get all cohorts as an array
     */
    const getCohortsArray = (0, react_1.useCallback)(() => {
        return Object.values(state.items);
    }, [state.items]);
    /**
     * Get a specific cohort by ID
     */
    const getCohortById = (0, react_1.useCallback)((id) => {
        return state.items[id];
    }, [state.items]);
    /**
     * Get only active cohorts
     */
    const getActiveCohorts = (0, react_1.useCallback)(() => {
        return Object.values(state.items).filter((cohort) => cohort.active !== false);
    }, [state.items]);
    const value = (0, react_1.useMemo)(() => (Object.assign(Object.assign({}, state), { fetchCohortsByOrg,
        fetchCohortById,
        getCohortsArray,
        getCohortById,
        getActiveCohorts })), [state, fetchCohortsByOrg, fetchCohortById, getCohortsArray, getCohortById, getActiveCohorts]);
    return (0, jsx_runtime_1.jsx)(CohortsContext.Provider, { value: value, children: children });
};
exports.CohortsProvider = CohortsProvider;
/**
 * Custom hook to access cohorts context
 */
const useCohorts = () => {
    const context = (0, react_1.useContext)(CohortsContext);
    if (!context) {
        throw new Error('useCohorts must be used within a CohortsProvider');
    }
    return context;
};
exports.useCohorts = useCohorts;
